import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Card, CardBody, CardHeader, Input, Row, Col, FormText } from "reactstrap";

import FormRowField from "shared/react_components/FormRowField";
import { CheckboxWithLabel } from "shared/formik/formik_helpers";
import { connect } from "formik";

const fields = [
  {
    name: "user.currentPassword",
    label: "Current Password"
  },
  { name: "user.newPassword", label: "New Password" },
  {
    name: "user.passwordConfirmation",
    label: "Confirm New Password"
  }
];

const UsersSecurityCardInner = ({
  twoFactorAuthRequired,
  sendNumberVerificationRequest,
  formik: {
    values: { user: { twoFactorAuthPhoneNumber, twoFactorAuthEnabled, phoneNumber } },
    initialValues: { user: { twoFactorAuthPhoneNumber: initialTwoFactorAuthPhoneNumber } },
    setFieldValue,
    setFieldError
  }
}) => {
  const [lastRequestedPhoneNumber, setLastRequestedPhoneNumber] = useState(null);
  const [verificationCodeSubmitting, setVerificationCodeSubmitting] = useState(false);
  const [checkedTwoFactorAuthNumber, setCheckedTwoFactorAuthNumber] = useState(false);

  const onVerificationRequestError = (message) => {
    setLastRequestedPhoneNumber("");
    setFieldError("user.twoFactorAuthPhoneNumber", message);
    setVerificationCodeSubmitting(false);
  };

  const onVerificationRequestSubmitted = () => {
    setLastRequestedPhoneNumber(twoFactorAuthPhoneNumber);
    setVerificationCodeSubmitting(false);
  };

  const sendVerificationRequest = () => {
    setFieldValue("user.twoFactorAuthVerificationCode", "");
    setVerificationCodeSubmitting(true);
    sendNumberVerificationRequest(
      twoFactorAuthPhoneNumber,
      onVerificationRequestSubmitted,
      onVerificationRequestError);
  };

  useEffect(() => {
    // If two factor auth is required and not selected, check the box.
    if (twoFactorAuthRequired && !twoFactorAuthEnabled) {
      setFieldValue("user.twoFactorAuthEnabled", true);
    }

    // Check only the first time twoFactorAuth is required
    if (twoFactorAuthEnabled && !checkedTwoFactorAuthNumber) {
      setCheckedTwoFactorAuthNumber(true);

      // If the number is blank, use the current mobile number.
      if (!twoFactorAuthPhoneNumber && phoneNumber) {
        setFieldValue("user.twoFactorAuthPhoneNumber", phoneNumber);
      }
    }
  });

  return (
    <Card>
      <CardHeader tag="h3">Security</CardHeader>
      <CardBody>
        <label>
          <strong>
            Passwords must be greater than 10 characters, contain a symbol, a capital letter, and a
            number.
          </strong>
        </label>
        {fields.map(fieldData => (
          <Row key={fieldData.name}>
            <Col md={9}>
              <FormRowField name={fieldData.name} label={fieldData.label}>
                {({ field }) => (
                  <Input {...field} type={"password"} placeholder={"Type something"} />
                )}
              </FormRowField>
            </Col>
          </Row>
        ))}
        <Row>
          <Col md={9}>
            <FormRowField name="user.twoFactorAuthEnabled">
              {({ field, form }) => (
                <div>
                  <CheckboxWithLabel
                    field={field}
                    form={form}
                    label="Two-Factor Authentication"
                    disabled={twoFactorAuthRequired}
                  />
                  <FormText color={"muted"}>
                    Protect your account with your password and your phone. &nbsp;
                    <a
                      data-mount="two-factor-auth"
                      href="https://invoca.force.com/community/s/article/Two-Factor-Authentication"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Click for more information.
                    </a>
                  </FormText>
                </div>
              )}
            </FormRowField>
          </Col>
        </Row>
        {(twoFactorAuthEnabled || twoFactorAuthRequired) && (
          <div>
            <Row>
              <Col md={9}>
                <FormRowField name="user.twoFactorAuthPhoneNumber" label="Mobile Phone">
                  {({ field }) => <Input {...field} />}
                </FormRowField>
              </Col>
            </Row>
            {twoFactorAuthPhoneNumber !== initialTwoFactorAuthPhoneNumber && (
              <Row>
                <Col md={9}>
                  {lastRequestedPhoneNumber !== twoFactorAuthPhoneNumber && (
                    <Button
                      name="sendInitialNotificationRequest"
                      color="primary"
                      disabled={verificationCodeSubmitting}
                      onClick={sendVerificationRequest}
                    >
                      Verify
                    </Button>
                  )}
                  {lastRequestedPhoneNumber === twoFactorAuthPhoneNumber && (
                    <FormRowField name="user.twoFactorAuthVerificationCode" label="Verification Code">
                      {({ field }) => (
                        <Row>
                          <Col md={8}>
                            <Input {...field} />
                            <FormText color={"muted"}>
                              Provide the code sent to your mobile phone. &nbsp;
                            </FormText>
                          </Col>
                          <Col md={4}>
                            <Button
                              name="sendAdditionalNotificationRequest"
                              color="secondary"
                              onClick={sendVerificationRequest}
                              disabled={verificationCodeSubmitting}
                            >
                              Send Again
                            </Button>
                          </Col>
                        </Row>
                      )}
                    </FormRowField>
                  )}
                </Col>
              </Row>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

UsersSecurityCardInner.propTypes = {
  twoFactorAuthRequired: PropTypes.bool,
  sendNumberVerificationRequest: PropTypes.func,
  formik: PropTypes.object
};

const UsersSecurityCard = connect(UsersSecurityCardInner);

export default UsersSecurityCard;
